// TourContext.js
import React, { createContext, useState, useEffect } from "react";

export const TourContext = createContext();

export const TourProvider = ({ children }) => {
  // Tour state management
  const [runTour, setRunTour] = useState(false);
  const [currentTourType, setCurrentTourType] = useState(null);
  const [completedTours, setCompletedTours] = useState(() => {
    const savedTours = localStorage.getItem("completedTours");
    return savedTours ? JSON.parse(savedTours) : [];
  });

  useEffect(() => {
    localStorage.setItem("completedTours", JSON.stringify(completedTours));
  }, [completedTours]);

  // Define tour content for each tour type - simplified for modal approach
  const tourContent = {
    onboarding: {
      title: "Welcome!",
      message:
        "This dashboard gives you an overview of your lending activities. Follow the steps below to get started.",
      details: [
        "Create loan products to define what you'll offer to borrowers",
        "Add borrowers to your system with their contact details",
        "Create loan applications for your borrowers",
      ],
    },
    loanProducts: {
      title: "Loan Products",
      message: "This is where you create and manage your loan products.",
      details: [
        "Click 'New Product' to create a loan product",
        "Define interest rates, terms, and other parameters",
        "Existing products will appear in the table below",
      ],
    },
    borrowers: {
      title: "Borrower Management",
      message: "Add and manage your borrowers here.",
      details: [
        "Click 'New Borrower' to add someone to your system",
        "Use the search box to find existing borrowers",
        "Click on a borrower to view their profile and loans",
      ],
    },
    loanApplication: {
      title: "Loan Application",
      message: "Create a new loan for a borrower.",
      details: [
        "Select a borrower from the dropdown",
        "Choose a loan product that you've created",
        "Enter the loan amount and other required details",
        "Submit the application to create the loan",
      ],
    },
  };

  // Function to start a specific tour
  const startTour = (tourType) => {
    if (!tourContent[tourType]) {
      console.error(`Tour type '${tourType}' not found`);
      return;
    }

    setCurrentTourType(tourType);
    setRunTour(true);
  };

  // Function to end current tour
  const endTour = (completeIt = true) => {
    if (completeIt && currentTourType) {
      setCompletedTours((prev) => [...prev, currentTourType]);
    }
    setRunTour(false);
    setCurrentTourType(null);
  };

  // Function to check if a tour has been completed
  const isTourCompleted = (tourType) => {
    return completedTours.includes(tourType);
  };

  // Function to reset all completed tours (for testing)
  const resetAllTours = () => {
    setCompletedTours([]);
    localStorage.removeItem("completedTours");
  };

  // Get current tour content
  const getCurrentTourContent = () => {
    return currentTourType ? tourContent[currentTourType] : null;
  };

  return (
    <TourContext.Provider
      value={{
        runTour,
        currentTourType,
        tourContent: getCurrentTourContent(),
        startTour,
        endTour,
        isTourCompleted,
        resetAllTours,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
